import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiErrorMessage, downloadFile } from '../api'
import { Tabs, type TabDef } from '../components/Tabs'
import { useDialogs } from '../components/Dialogs'
import {
  customersApi, deliveryAreasApi, occasionsApi, setCustomerOrder, invoicesApi,
  type CustomerListItem, type DeliveryArea, type DeliveryOccasion, type WeekInterval,
} from '../crmApi'

const INTERVALS: { value: WeekInterval; label: string }[] = [
  { value: 'EveryWeek', label: 'Varje vecka' },
  { value: 'EverySecondWeek', label: 'Varannan vecka' },
  { value: 'EveryThirdWeek', label: 'Var tredje vecka' },
  { value: 'EveryFourthWeek', label: 'Var fjärde vecka' },
]

export function DeliveryAreaDetail() {
  const { confirm, notify } = useDialogs()
  const { id } = useParams()
  const areaId = Number(id)
  const navigate = useNavigate()

  const [area, setArea] = useState<DeliveryArea | null>(null)
  const [occasions, setOccasions] = useState<DeliveryOccasion[]>([])
  const [customers, setCustomers] = useState<CustomerListItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [orderDirty, setOrderDirty] = useState(false)

  // occasion form state
  const [singleDate, setSingleDate] = useState('')
  const [recur, setRecur] = useState<{ startDate: string; endDate: string; interval: WeekInterval }>(
    { startDate: '', endDate: '', interval: 'EverySecondWeek' })

  async function loadOccasions() {
    setOccasions(await occasionsApi.list(areaId, true))
  }

  useEffect(() => {
    Promise.all([
      deliveryAreasApi.get(areaId).then(setArea),
      occasionsApi.list(areaId, true).then(setOccasions),
      customersApi.list({ deliveryAreaId: areaId }).then(setCustomers),
    ]).catch((e) => setError(describe(e)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId])

  async function addSingle() {
    if (!singleDate) return
    try { await occasionsApi.createSingle(areaId, singleDate); setSingleDate(''); await loadOccasions() }
    catch (e) { setError(describe(e)) }
  }

  async function addRecurring() {
    if (!recur.startDate || !recur.endDate) return
    try {
      const created = await occasionsApi.createRecurring(areaId, recur)
      setError(created.length ? null : 'Inga nya leveranstillfällen skapades (alla fanns redan eller låg i dåtid).')
      await loadOccasions()
    } catch (e) { setError(describe(e)) }
  }

  async function removeOccasion(o: DeliveryOccasion) {
    if (!(await confirm(`Radera leveranstillfället ${o.date}?`, { danger: true, confirmLabel: 'Radera' }))) return
    try { await occasionsApi.remove(areaId, o.id); await loadOccasions() }
    catch (e) { setError(describe(e)) }
  }

  async function generateInvoices(o: DeliveryOccasion) {
    if (!(await confirm(`Stäng tillfället ${o.date} och generera fakturor från dess ordrar?`, { confirmLabel: 'Fakturera' }))) return
    try {
      const res = await invoicesApi.generate(o.id)
      setError(null)
      await notify(`${res.created} faktura/-or skapade.`)
      await loadOccasions()
    } catch (e) { setError(describe(e)) }
  }

  async function downloadBatch(o: DeliveryOccasion) {
    try { await downloadFile(invoicesApi.batchPdfPath(o.id), `fakturor-${o.date}.pdf`) }
    catch (e) { setError(describe(e)) }
  }

  function moveCustomer(index: number, dir: -1 | 1) {
    const target = index + dir
    if (target < 0 || target >= customers.length) return
    const next = [...customers]
    ;[next[index], next[target]] = [next[target], next[index]]
    setCustomers(next)
    setOrderDirty(true)
  }

  async function saveOrder() {
    try {
      await setCustomerOrder(areaId, customers.map((c) => c.id))
      setOrderDirty(false)
    } catch (e) { setError(describe(e)) }
  }

  if (error && !area) return <p className="error">{error}</p>
  if (!area) return <p className="muted">Laddar…</p>

  const occasionsTab = (
    <section className="card">
        <h2>Leveranstillfällen (kommande)</h2>
        <div className="row-form">
          <input type="date" value={singleDate} onChange={(e) => setSingleDate(e.target.value)} />
          <button className="primary" onClick={() => void addSingle()}>Lägg till tillfälle</button>
        </div>
        <div className="row-form" style={{ marginTop: '0.6rem' }}>
          <label className="inline">Återkommande:</label>
          <input type="date" value={recur.startDate} onChange={(e) => setRecur({ ...recur, startDate: e.target.value })} />
          <span>–</span>
          <input type="date" value={recur.endDate} onChange={(e) => setRecur({ ...recur, endDate: e.target.value })} />
          <select value={recur.interval} onChange={(e) => setRecur({ ...recur, interval: e.target.value as WeekInterval })}>
            {INTERVALS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
          </select>
          <button onClick={() => void addRecurring()}>Generera</button>
        </div>

        <table className="grid" style={{ marginTop: '0.8rem' }}>
          <thead><tr><th>Datum</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {occasions.map((o) => (
              <tr key={o.id}>
                <td>{o.date}</td>
                <td>{o.isClosed ? 'Stängt' : 'Öppet'}</td>
                <td className="actions">
                  <button onClick={() => void generateInvoices(o)}>Fakturera</button>
                  <button onClick={() => void downloadBatch(o)}>Fakturor (PDF)</button>
                  <button onClick={() => void removeOccasion(o)}>Radera</button>
                </td>
              </tr>
            ))}
            {occasions.length === 0 && <tr><td colSpan={3} className="muted">Inga kommande leveranstillfällen.</td></tr>}
          </tbody>
        </table>
    </section>
  )

  const orderTab = (
    <section className="card">
        <div className="page-head">
          <h2>Leveransordning</h2>
          <button className="primary" disabled={!orderDirty} onClick={() => void saveOrder()}>Spara ordning</button>
        </div>
        <table className="grid">
          <thead><tr><th>#</th><th>Kund</th><th>Adress</th><th></th></tr></thead>
          <tbody>
            {customers.map((c, i) => (
              <tr key={c.id}>
                <td>{i + 1}</td>
                <td>{c.firstName} {c.lastName ?? ''}{!c.isActive && <span className="badge muted-badge">Inaktiv</span>}</td>
                <td>{c.homePhone}</td>
                <td className="actions">
                  <button disabled={i === 0} onClick={() => moveCustomer(i, -1)}>↑</button>
                  <button disabled={i === customers.length - 1} onClick={() => moveCustomer(i, 1)}>↓</button>
                </td>
              </tr>
            ))}
            {customers.length === 0 && <tr><td colSpan={4} className="muted">Inga kunder i området.</td></tr>}
          </tbody>
        </table>
    </section>
  )

  const tabs: TabDef[] = [
    { id: 'occasions', label: 'Leveranstillfällen', content: occasionsTab },
    { id: 'order', label: 'Leveransordning', content: orderTab },
  ]

  return (
    <div>
      <div className="page-head">
        <h1>{area.name}</h1>
        <button onClick={() => navigate('/delivery-areas')}>Tillbaka</button>
      </div>
      {error && <p className="error">{error}</p>}
      <Tabs tabs={tabs} />
    </div>
  )
}

const describe = apiErrorMessage
